import { qs, qsa, pad } from "../inc/dom.js";

const PLAYER_POLL_MS = 200;
const PLAYER_POLL_LIMIT = 50;
const ACTIVE_TOLERANCE = 0.5;
const IDLE_DELAY_MS = 2500;

const toSeconds = (stamp) => {
    const parts = String(stamp || "").split(":");
    if (!parts.length) return null;
    const values = parts.map((part) => parseInt(part, 10));
    if (values.some((value) => Number.isNaN(value))) return null;
    return values.reduce((total, value) => total * 60 + value, 0);
};

const getVimeoRegistry = () => {
    const paged = window.pagevideos && window.pagevideos.loaded;
    if (paged && paged.vimeo) return paged.vimeo;
    if (window.oivideos && window.oivideos.vimeo) return window.oivideos.vimeo;
    return null;
};

const getPlayer = (iframe) => {
    const registry = getVimeoRegistry();
    const entry = registry && registry[iframe.id];
    if (entry && entry.player) return entry.player;
    if (entry && window.pagevideos && window.pagevideos.loadVideo) {
        window.pagevideos.loadVideo(iframe.id);
        return null;
    }
    if (window.Vimeo && window.Vimeo.Player && iframe.getAttribute("src")) {
        return new window.Vimeo.Player(iframe);
    }
    return null;
};

const waitForPlayer = (iframe) =>
    new Promise((resolve) => {
        let tries = 0;
        const check = () => {
            const player = getPlayer(iframe);
            if (player) {
                resolve(player);
                return;
            }
            tries += 1;
            if (tries > PLAYER_POLL_LIMIT) {
                resolve(null);
                return;
            }
            window.setTimeout(check, PLAYER_POLL_MS);
        };
        check();
    });





const initTakesSlider = () => {
    const el = qs("[data-reels-swiper]");
    if (!el || !window.Swiper) return;
    if (el.swiper) {
        el.swiper.update();
        return;
    }
    const count = qs("[data-reels-count]");
    const prev = qs("[data-reels-prev]");
    const next = qs("[data-reels-next]");
    const render = (swiper) => {
        const total = swiper.snapGrid.length;
        const current = swiper.snapIndex + 1;
        if (count) count.textContent = `${pad(current)} / ${pad(total)}`;
        if (prev) prev.disabled = swiper.isBeginning;
        if (next) next.disabled = swiper.isEnd;
    };
    new window.Swiper(el, {
        slidesPerView: 2,
        slidesPerGroup: 2,
        spaceBetween: 20,
        threshold: 10,
        watchOverflow: true,
        breakpoints: {
            640: { slidesPerView: 3, slidesPerGroup: 3 },
            1024: { slidesPerView: 5, slidesPerGroup: 5 },
        },
        navigation: {
            prevEl: "[data-reels-prev]",
            nextEl: "[data-reels-next]",
        },
        on: {
            init: render,
            slideChange: render,
            resize: render,
        },
    });
};

const bindRows = (player) => {
    const rows = qsa("[data-chapter]")
        .map((row) => ({ row, start: toSeconds(row.dataset.chapter) }))
        .filter((entry) => entry.start !== null);
    if (!rows.length) return;

    let activeStart = null;
    const setActive = (start) => {
        if (start === activeStart) return;
        activeStart = start;
        rows.forEach((entry) => {
            entry.row.classList.toggle("is-active", entry.start === start);
        });
    };

    rows.forEach((entry) => {
        entry.row.addEventListener("click", (event) => {
            event.preventDefault();
            setActive(entry.start);
            player.setCurrentTime(entry.start).catch(() => {});
            player.play().catch(() => {});
        });
    });

    player.on("timeupdate", (data) => {
        let active = null;
        rows.forEach((entry) => {
            if (data.seconds + ACTIVE_TOLERANCE >= entry.start) active = entry.start;
        });
        setActive(active);
    });
};

const bindIdleState = (player) => {
    const reels = qs("[data-reels]");
    if (!reels) return;
    let timer = null;
    const wake = () => {
        reels.classList.remove("is-idle");
        window.clearTimeout(timer);
        timer = window.setTimeout(() => reels.classList.add("is-idle"), IDLE_DELAY_MS);
    };
    ["mousemove", "pointerdown", "keydown", "scroll", "touchstart"].forEach((type) => {
        window.addEventListener(type, wake, { passive: true });
    });
    player.on("play", () => {
        reels.classList.add("is-playing");
        wake();
    });
    player.on("pause", () => reels.classList.remove("is-playing"));
    player.on("ended", () => reels.classList.remove("is-playing"));
};

const initVideoDock = (zone) => {
    const frame = qs(".video-frame", zone);
    const bar = qs(".article-metabar");
    const nav = document.getElementById("site-nav");
    if (!frame || !bar) return;
    let ticking = false;
    const apply = () => {
        const navBottom = nav ? nav.getBoundingClientRect().bottom : 0;
        const docked = bar.getBoundingClientRect().top <= navBottom;
        if (docked === zone.classList.contains("is-docked")) return;
        if (docked) {
            zone.style.setProperty("--video-h", `${frame.offsetHeight}px`);
        }
        zone.classList.toggle("is-docked", docked);
    };
    const schedule = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
            ticking = false;
            apply();
        });
    };
    apply();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule, { passive: true });
};

const initLiveDetail = async () => {
    const zone = qs(".video-zone");
    if (!zone) return;
    const iframe = qs("iframe.oi-video-iframe", zone);
    if (!iframe || !iframe.id) return;

    initVideoDock(zone);
    initTakesSlider();

    const player = await waitForPlayer(iframe);
    if (!player) return;

    bindRows(player);
    bindIdleState(player);
};

initLiveDetail();
